import { useCallback, useEffect, useRef, useState } from 'react';
import { useLite } from '../utils/deviceLite';

/**
 * Keeps a modal mounted through its exit animation before calling onClose.
 * Returns { mounted, closing, requestClose }.
 *
 * Under lite (utils/deviceLite, 2026-11-03) there is no exit animation to wait for — styles/lite.css ends it at once —
 * so the sheet closes on the tap: the colour sheet's add reaches the cart, and a checkout sheet's step goes on, without
 * a quarter of a second of nothing on an old phone (the owner: "the animation of opening are slow").
 */
export function useAnimatedModal(open, onClose, requestedMs = 280) {
  const lite = useLite();
  const durationMs = lite ? 0 : requestedMs;
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setClosing(false);
      setMounted(true);
      return undefined;
    }
    if (!mounted) return undefined;
    setClosing(true);
    timerRef.current = setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, durationMs);
    return () => clearTimeout(timerRef.current);
  }, [open, mounted, durationMs]);

  const requestClose = useCallback(() => {
    if (closing) return;
    clearTimeout(timerRef.current);
    setClosing(true);
    timerRef.current = setTimeout(() => {
      setMounted(false);
      setClosing(false);
      onClose?.();
    }, durationMs);
  }, [closing, onClose, durationMs]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { mounted, closing, requestClose };
}

export default useAnimatedModal;
